import { useMemo, useRef, useState } from 'react';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import {
  ZONE_PREFERENCE_LIST,
  assertIanaZone,
  inferMapTimeZone,
  type MapTimeZoneResult,
} from '@mm-shared/intervals';
import type { MilestoneMap1 } from '@mm-shared/types/MilestoneMap1';
import type { Objective1 } from '@mm-shared/types/Objective1';
import { Button } from '../../components_catalyst/button';
import { Description, Field, Label } from '../../components_catalyst/fieldset';
import { Text } from '../../components_catalyst/text';

let cachedZoneList: string[] | undefined;

/**
 * Selectable IANA zones: `Intl.supportedValuesOf('timeZone')` minus fixed offsets (`Etc/GMT+5`,
 * `-04:00`, ...), which `assertIanaZone` rejects (intervals README INV-11).
 */
export function getSelectableTimeZones(): string[] {
  if (!cachedZoneList) {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    const all = new Set<string>([...(supported ?? []), ...ZONE_PREFERENCE_LIST]);
    cachedZoneList = Array.from(all)
      .filter((z) => {
        try {
          assertIanaZone(z);
          return true;
        } catch {
          return false;
        }
      })
      .sort();
  }
  return cachedZoneList;
}

/**
 * Statuses used for zone inference: only objectives that belong to this map
 * (`map.objectiveUuids`). The Redux objectives store also holds objectives from other maps
 * (relatives, descendants, previously visited maps), which must not influence the owner's
 * pre-filled zone (INV-TZ-1).
 */
export function collectMapStatuses(
  map: Pick<MilestoneMap1, 'objectiveUuids'> | undefined | null,
  objectives: Array<Pick<Objective1, 'uuid' | 'statuses'>>,
): Array<{ forIntervalEnding: string }> {
  const own = new Set(map?.objectiveUuids ?? []);
  return objectives.filter((o) => own.has(o.uuid)).flatMap((o) => o.statuses ?? []);
}

/** Human-readable inference line (no trailing punctuation), e.g. "Inferred: America/New_York (high confidence)". */
export function describeInference(result: MapTimeZoneResult): string {
  if (result.source === 'default') {
    return 'No status data to infer a time zone from yet';
  }
  if (result.confidence === 'ambiguous') {
    const zones = result.candidates && result.candidates.length > 0 ? result.candidates : [result.zone];
    const shown = zones.slice(0, 4).join(', ') + (zones.length > 4 ? ', and more' : '');
    return `Ambiguous: ${shown} (low confidence)`;
  }
  return `Inferred: ${result.zone} (high confidence)`;
}

export type MapTimeZoneSettingProps = {
  map: MilestoneMap1;
  /** Statuses on the map's objectives; only `forIntervalEnding` is used, for inference. */
  statuses: Array<{ forIntervalEnding: string }>;
  /** Owner-only (`getPermissionsForRole(role).canManageSettings`). */
  canManage: boolean;
  /** Called with the chosen IANA zone, or `null` to clear. */
  onChange: (timeZone: string | null) => void;
};

const AMBIGUOUS_CONSEQUENCE =
  "Until you choose one, AI agents can read this map but can't add statuses to it.";

const EXPLANATION =
  'This zone tells MCP agents (AI assistants connected to this map) which column a status belongs in. ' +
  'It does not change how dates appear in your browser grid today.';

/**
 * Owner-only map time-zone picker (Bead mr-d5js.28). Pre-filled with the stored zone, else the
 * inference result. Non-owners see read-only text.
 */
export function MapTimeZoneSetting({ map, statuses, canManage, onChange }: MapTimeZoneSettingProps) {
  // Local copy so the UI reflects a save without re-fetching the map (which would reset the form).
  const [stored, setStored] = useState<string | undefined>(map.timeZone || undefined);
  const inference = useMemo(() => inferMapTimeZone(map, statuses), [map, statuses]);
  const inferenceText = describeInference(inference);
  const suggestion = inference.source === 'default' ? null : inference.zone;

  const [selected, setSelected] = useState<string | null>(stored ?? suggestion);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const unconfirmed = inference.confidence === 'ambiguous';

  const zones = useMemo(() => getSelectableTimeZones(), []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/\s+/g, '_');
    return q ? zones.filter((z) => z.toLowerCase().includes(q)) : zones;
  }, [zones, query]);

  if (!canManage) {
    return (
      <div className="space-y-1" data-testid="map-time-zone-readonly">
        <h3 className="text-sm font-medium">Time zone</h3>
        <Text>
          {stored
            ? `${stored} (set by the map owner)`
            : unconfirmed
              ? "Not set. The map owner hasn't confirmed a zone yet."
              : inferenceText}
        </Text>
        <Text>{EXPLANATION}</Text>
        <Text>Only the map owner can change the time zone.</Text>
      </div>
    );
  }

  const canSave = !!selected && selected !== stored;

  return (
    <Field className="space-y-2" data-testid="map-time-zone-editor">
      <Label>Time zone</Label>
      <Description>
        {stored
          ? `Current: ${stored}. ${EXPLANATION}`
          : `${inferenceText}. ${unconfirmed ? `${AMBIGUOUS_CONSEQUENCE} ` : ''}${EXPLANATION}`}
      </Description>
      <Combobox
        value={selected}
        onChange={(zone: string | null) => {
          setSelected(zone);
          setMessage('');
        }}
        onClose={() => setQuery('')}
      >
        {/* Enter must never submit an enclosing form (Headless UI lets Enter through when the list is closed). */}
        <div
          className="relative"
          data-slot="control"
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
        >
          <ComboboxInput
            ref={inputRef}
            placeholder="Search time zones, e.g. New York"
            displayValue={(zone: string | null) => zone ?? ''}
            onChange={(e) => setQuery(e.target.value)}
            className={clsx(
              'w-full rounded-lg border border-zinc-950/10 bg-white py-1.5 pl-3 pr-10 text-sm/6 text-zinc-950',
              'placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
              'dark:border-white/10 dark:bg-white/5 dark:text-white',
            )}
          />
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="size-5 text-zinc-400" aria-hidden="true" />
          </span>
          <ComboboxOptions
            anchor="bottom start"
            className={clsx(
              'w-[var(--input-width)] rounded-xl border border-zinc-950/5 bg-white p-1 dark:border-white/10 empty:hidden',
              'max-h-60 overflow-auto shadow-lg ring-1 ring-zinc-950/10 dark:bg-zinc-800 dark:ring-white/10',
            )}
          >
            {filtered.map((zone) => (
              <ComboboxOption
                key={zone}
                value={zone}
                className={clsx(
                  'group flex cursor-default select-none items-center gap-2 rounded-lg px-3 py-1.5',
                  'text-sm/6 text-zinc-950 dark:text-white',
                  'data-[focus]:bg-blue-500 data-[focus]:text-white',
                )}
              >
                <CheckIcon className="invisible size-4 group-data-[selected]:visible" aria-hidden="true" />
                <span>{zone}</span>
              </ComboboxOption>
            ))}
            {filtered.length === 0 && <div className="px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400">No matching time zone</div>}
          </ComboboxOptions>
        </div>
      </Combobox>
      <div className="flex gap-2">
        <Button
          type="button"
          disabled={!canSave}
          onClick={() => {
            if (!selected) return;
            onChange(selected);
            setStored(selected);
            setMessage(`Time zone set to ${selected}. MCP agents will place statuses using this zone.`);
          }}
        >
          {stored ? 'Change time zone' : 'Save time zone'}
        </Button>
        {stored && (
          <Button
            type="button"
            outline
            onClick={() => {
              onChange(null);
              setStored(undefined);
              setSelected(suggestion);
              setMessage(
                unconfirmed
                  ? `Time zone cleared. ${AMBIGUOUS_CONSEQUENCE}`
                  : `Time zone cleared. AI agents will use the inferred zone (${inference.zone}).`,
              );
              // The Clear button unmounts; keep keyboard/screen-reader users in place.
              inputRef.current?.focus();
            }}
          >
            Clear time zone
          </Button>
        )}
      </div>
      <p role="status" className="text-sm text-zinc-600 dark:text-zinc-400">
        {message}
      </p>
    </Field>
  );
}
